import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-toastify';
import { Delete } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import PracticeBanner from '../components/PracticeBanner';
import { afterSignIn } from '../utils/returnTo';

const PIN_LENGTH = 6;

export default function Login() {
  const [digits, setDigits] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const { loginWithToken, company, isPractice } = useAuth();
  const navigate = useNavigate();
  const hiddenRef = useRef<HTMLInputElement>(null);
  const [asRole, setAsRole] = useState<string | null>(null);

  // Practice: one tap per role, no PINs (owner's ask, Sep 16 2026).
  async function loginAs(role: string) {
    setAsRole(role);
    try {
      const res = await api.post('/practice/login-as', { role });
      if (role === 'client') { window.location.assign(res.data.url); return; }
      const { token, user } = res.data;
      loginWithToken(token, user);
      navigate(afterSignIn(user?.role));
    } catch (err: any) {
      toast.error(err.response ? (err.response.data?.message || 'Could not sign in') : 'Could not connect to server — check Wi-Fi');
      setAsRole(null);
    }
  }

  // Auto-submit when 6 digits entered
  useEffect(() => {
    if (digits.length === PIN_LENGTH) {
      submitPin(digits.join(''));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits]);

  // Physical keyboard support
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (loading || isPractice) return;
      if (e.key >= '0' && e.key <= '9') {
        setDigits(prev => prev.length < PIN_LENGTH ? [...prev, e.key] : prev);
      } else if (e.key === 'Backspace') {
        setDigits(prev => prev.slice(0, -1));
      } else if (e.key === 'Enter') {
        if (digits.length === PIN_LENGTH) submitPin(digits.join(''));
      } else if (e.key === 'Escape' || e.key === 'c' || e.key === 'C') {
        setDigits([]);
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, digits, isPractice]);

  async function submitPin(pin: string) {
    setLoading(true);
    try {
      const res = await api.post('/auth/pin-login', { pin });
      const { token, user } = res.data;
      loginWithToken(token, user);
      // Back to the crew page they were sent here from (an open-shift link), else home.
      navigate(afterSignIn(user?.role));
    } catch (err: any) {
      // Network errors have no `.response` — show a distinct message so
      // the crew knows it's a connection problem, not a wrong PIN.
      const msg = err.response
        ? (err.response.status === 401 ? 'Wrong PIN — try again' : (err.response.data?.message || 'Wrong PIN — try again'))
        : 'Could not connect to server — check Wi-Fi';
      setPinError(msg);
      toast.error(msg);
      setShake(true);
      setTimeout(() => { setShake(false); setDigits([]); }, 600);
    } finally {
      setLoading(false);
    }
  }

  function press(d: string) {
    if (digits.length < PIN_LENGTH && !loading) {
      setPinError(null);
      setDigits(prev => [...prev, d]);
    }
  }

  function backspace() {
    setDigits(prev => prev.slice(0, -1));
  }

  function clear() {
    setDigits([]);
  }

  const PAD = [
    ['1','2','3'],
    ['4','5','6'],
    ['7','8','9'],
    ['C','0','⌫'],
  ];

  const ROLES = [
    { key: 'admin',     label: 'Admin',     hint: 'The office: routes, bids, billing' },
    { key: 'crew',      label: 'Crew',      hint: "Pat Practice's day on the phone" },
    { key: 'bidder',    label: 'Bidder',    hint: 'On-site quotes and bids' },
    { key: 'manager',   label: 'Manager',   hint: 'The yard check, the lawn inspections, changes that wait for the owner' },
    { key: 'client',    label: 'Client',    hint: "A customer's own page" },
  ];

  if (isPractice) {
    return (
      <div className="min-h-screen flex">
        <PracticeBanner fixed />
        <div className="flex-1 flex flex-col justify-center py-12 px-4 items-center">
          <div className="w-full max-w-xs">
            <div className="text-center mb-8">
              <div className="mx-auto mb-4 flex justify-center"><BrandLogo size={72} /></div>
              <h1 className="text-2xl font-black uppercase tracking-tight text-gray-900">Keep Austin Mowed — Practice</h1>
              <p className="text-sm text-gray-500 mt-1">Pick who you want to be. No PIN here.</p>
            </div>
            <div className="space-y-2.5">
              {ROLES.map(r => (
                <button key={r.key} type="button" onClick={() => loginAs(r.key)} disabled={!!asRole}
                  className="w-full text-left rounded-xl border border-gray-200 bg-white shadow-sm px-4 py-3.5 hover:border-brand-300 hover:bg-brand-50 active:bg-brand-100 disabled:opacity-50 transition-all">
                  <span className="block text-base font-semibold text-gray-900">{asRole === r.key ? 'Signing in…' : r.label}</span>
                  <span className="block text-xs text-gray-500">{r.hint}</span>
                </button>
              ))}
            </div>
            <p className="text-center text-xs text-gray-500 mt-6">Fake clients and jobs. Nothing here is real.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <PracticeBanner fixed />
      {/* Left — PIN pad */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 items-center">
        <div className="w-full max-w-xs">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="mx-auto mb-4 flex justify-center"><BrandLogo size={72} /></div>
            <h1 className="text-2xl font-black uppercase tracking-tight text-gray-900">Keep Austin Mowed</h1>
            <p className="text-sm text-gray-500 mt-1">Enter your 6-digit code</p>
          </div>

          {/* PIN dots */}
          <div className={`flex justify-center gap-3 mb-8 ${shake ? 'animate-[shake_0.5s_ease-in-out]' : ''}`}>
            {Array.from({ length: PIN_LENGTH }).map((_, i) => (
              <div
                key={i}
                className={`w-4 h-4 rounded-full border-2 transition-all duration-150 ${
                  i < digits.length
                    ? 'bg-brand-600 border-brand-600 scale-110'
                    : 'bg-transparent border-gray-300'
                }`}
              />
            ))}
          </div>

          {pinError && <p className="text-center text-sm font-semibold text-red-600 -mt-4 mb-4">{pinError}</p>}

          {/* Hidden input for mobile keyboard (fallback) */}
          <input
            ref={hiddenRef}
            type="tel"
            inputMode="numeric"
            className="sr-only"
            value={digits.join('')}
            onChange={e => {
              const val = e.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH);
              setDigits(val.split(''));
            }}
          />

          {/* Number pad */}
          <div className="grid grid-cols-3 gap-3">
            {PAD.flat().map(key => {
              const isAction = key === 'C' || key === '⌫';
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    if (key === '⌫') backspace();
                    else if (key === 'C') clear();
                    else press(key);
                  }}
                  disabled={loading}
                  className={`py-4 rounded-xl text-lg font-semibold transition-all active:scale-95 select-none ${
                    isAction
                      ? 'text-gray-500 bg-gray-100 hover:bg-gray-200 active:bg-gray-300'
                      : 'text-gray-900 bg-white border border-gray-200 shadow-sm hover:bg-brand-50 hover:border-brand-300 active:bg-brand-100'
                  } disabled:opacity-50`}
                >
                  {key === '⌫' ? <Delete className="h-5 w-5 mx-auto" /> : key}
                </button>
              );
            })}
          </div>

          {loading && (
            <div className="text-center mt-6">
              <div className="inline-flex items-center gap-2 text-brand-600 text-sm">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600" />
                Verifying…
              </div>
            </div>
          )}

          <p className="text-center text-xs text-gray-500 mt-6">
            Crew: ask Michael for your code.
          </p>
          {/* Homeowners land here from a search now and then — point them to their own link. */}
          <div className="mt-6 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 text-center">
            <p className="text-sm font-semibold text-brand-900">Customer?</p>
            <p className="text-xs text-brand-800 mt-0.5">Use the link we text or email you — this screen is for the crew.</p>
            <a href={`sms:${company.phone_raw}`} className="inline-block mt-2 text-xs font-semibold text-brand-700 underline">Need your link? Text {company.phone}</a>
          </div>
        </div>
      </div>

      {/* Right — branding */}
      <div className="hidden lg:flex relative w-0 flex-1">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-600 via-brand-700 to-brand-900 dark:via-brand-600 dark:to-plum flex items-center justify-center">
          <div className="text-center text-snow px-8">
            <div className="mx-auto mb-6 flex justify-center rounded-3xl bg-white/95 p-5 shadow-pop"><BrandLogo size={160} /></div>
            <h3 className="text-3xl font-bold mb-2">Keep Austin Mowed</h3>
            <p className="text-brand-100 text-lg dark:text-snow/90">The crew and office side of Keep Austin Mowed</p>
            <p className="text-brand-200 text-sm mt-1 dark:text-snow/75">Customers: your own page is the link we text you.</p>
            <div className="mt-10 space-y-3 text-sm text-brand-200 dark:text-snow/75">
              {['Schedule & assign jobs', 'Crew daily schedule + pay', 'Job completion photos', 'Customer completion messages'].map(f => (
                <div key={f} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-brand-300 rounded-full dark:bg-snow/60" />
                  {f}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
